import type { NavIconName } from '../nav';

/**
 * The sidebar's icons: one set, drawn here, no library.
 *
 * Fifteen 18px glyphs is a few hundred bytes of path data inline; the smallest
 * icon package that would have drawn them is tens of kilobytes and a second
 * source of truth for what the brand's line weight is. They are all the same
 * 24-unit grid, the same 1.7 stroke, no fills, `currentColor` throughout — so
 * an active item's icon changes colour with its label and nothing has to be
 * restyled per state.
 *
 * `aria-hidden` on every one of them: the label beside it already says the
 * word, and an icon that repeats it makes a screen reader say it twice.
 */
const PATHS: Record<IconName, string[]> = {
  /* A megaphone: a campaign is somebody pointing creators at their material. */
  campaigns: ['M4 10v4a1 1 0 0 0 1 1h2l5 4V5L7 9H5a1 1 0 0 0-1 1Z', 'M16.5 8.5a4.5 4.5 0 0 1 0 7'],
  submit: ['M12 16V4', 'M8 8l4-4 4 4', 'M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3'],
  submissions: ['M9 6h11', 'M9 12h11', 'M9 18h11', 'M4.5 6h.01', 'M4.5 12h.01', 'M4.5 18h.01'],
  leaderboard: ['M5 20v-7', 'M12 20V4', 'M19 20v-10'],
  connections: [
    'M10.5 13.5a4 4 0 0 0 5.9.3l2.3-2.3a4 4 0 0 0-5.66-5.66l-1.3 1.3',
    'M13.5 10.5a4 4 0 0 0-5.9-.3l-2.3 2.3a4 4 0 1 0 5.66 5.66l1.3-1.3',
  ],
  wallet: ['M4 7a2 2 0 0 1 2-2h11v3', 'M4 7v10a2 2 0 0 0 2 2h13a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1H4', 'M16.5 13h.01'],
  /* One person handing something to another: a referral is a share, not a
     second wallet and not a second set of users. */
  referrals: [
    'M8.5 11a3.25 3.25 0 1 0 0-6.5 3.25 3.25 0 0 0 0 6.5Z',
    'M2.5 19.5a6 6 0 0 1 12 0',
    'M17.5 5.5v6',
    'M14.5 8.5h6',
  ],
  profile: ['M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z', 'M4.5 20a7.5 7.5 0 0 1 15 0'],
  overview: ['M4 4h6v7H4Z', 'M14 4h6v4h-6Z', 'M14 12h6v8h-6Z', 'M4 15h6v5H4Z'],
  review: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'M8.5 12.2l2.4 2.4 4.6-5'],
  creators: [
    'M9.5 12a3.75 3.75 0 1 0 0-7.5 3.75 3.75 0 0 0 0 7.5Z',
    'M2.5 20a7 7 0 0 1 14 0',
    'M16.5 5.4a3.75 3.75 0 0 1 0 7.2',
    'M18 15.2A6 6 0 0 1 21.5 20',
  ],
  invitations: ['M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z', 'm3.5 7.5 8.5 6 8.5-6'],
  payouts: ['M2.5 7h19v10h-19Z', 'M12 14.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z', 'M6 10.5h.01', 'M18 13.5h.01'],
  ledger: ['M5.5 4.5A2 2 0 0 1 7.5 2.5H19v17H7.5a2 2 0 0 0-2 2Z', 'M9.5 7.5h6', 'M9.5 11.5h6'],
  reports: ['M4 19.5V5', 'M4 19.5h16', 'M8 16V11', 'M12.5 16V7', 'M17 16v-6'],
  users: ['M3 6h18v13H3Z', 'M9 13a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z', 'M6.5 16.5a3 3 0 0 1 5 0', 'M14.5 10h4', 'M14.5 14h4'],
  outbox: ['M21.5 3 10.5 14', 'M21.5 3 15 21l-4.5-7-7-4.5Z'],
  /* Three sliders: settings are things you set, not a machine you open. */
  settings: ['M4 7h16', 'M4 12h16', 'M4 17h16', 'M9 5v4', 'M15 10v4', 'M7 15v4'],
  /* A speech bubble with a question in it: support is asking a person. */
  support: [
    'M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-7l-4.5 3.5V17H6a2 2 0 0 1-2-2Z',
    'M10 9.5a2 2 0 1 1 2.8 1.8c-.6.3-.8.7-.8 1.2',
    'M12 15h.01',
  ],
  menu: ['M4 7h16', 'M4 12h16', 'M4 17h16'],
  close: ['M6 6l12 12', 'M18 6 6 18'],
  signout: ['M14.5 12h-10', 'M8 8l-4 4 4 4', 'M11 4.5h6a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2h-6'],
  /* The theme control's three. A monitor for 'whatever this machine says', a
     sun for light and a moon for dark - the only three glyphs a person does
     not have to be taught in a theme switcher. */
  'theme-system': ['M3 5.5h18v11H3Z', 'M8.5 20.5h7', 'M12 16.5v4'],
  'theme-light': [
    'M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
    'M12 2.5v2',
    'M12 19.5v2',
    'M2.5 12h2',
    'M19.5 12h2',
    'M5.3 5.3 6.7 6.7',
    'M17.3 17.3l1.4 1.4',
    'M18.7 5.3 17.3 6.7',
    'M6.7 17.3 5.3 18.7',
  ],
  'theme-dark': ['M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z'],
};

/** Every glyph this set can draw: the sidebar's, the chrome's, the theme's. */
export type IconName =
  | NavIconName
  | 'menu'
  | 'close'
  | 'signout'
  | 'theme-system'
  | 'theme-light'
  | 'theme-dark';

export function NavIcon({
  name,
  size = 18,
}: {
  name: IconName;
  size?: number;
}): React.JSX.Element {
  return (
    <svg
      className="nav-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}
